import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { Container, Grid, Typography } from '@mui/material';
import { IncidentReport } from '../../../components/incident/IncidentReport';
import { di } from '../../../state/inversify.config';
import { GlobalQueryParams } from '../../../state/queryParams/queryParams.interface';
import { useQuerySync } from '../../../state/queryParams/useQuerySync';
import { AssessmentQueryParamService } from './assessment.queryParam.service';
import { AssessmentPageQuery } from './assessment.queryParams.interface';
import { AssessmentDomainService } from './assessmentDomainService';
import { Assessment } from '../../../state/reduxRepositories/assessments/assessments.interfaces';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';
import { dateTime } from '../../../time/datetime';
import { AuthService } from '../../../state/helperServices/auth/auth.service';
import { Button } from '../../../components/button/button';

export function AssessmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const assessmentQueryParamService = di.get<AssessmentQueryParamService>(
    AssessmentQueryParamService,
  );
  useQuerySync<AssessmentPageQuery & GlobalQueryParams>(
    assessmentQueryParamService,
  );

  // TODO: move into a domainService and only return accountType or even better find a way so that you don't have to send down the accountType
  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const assessmentDomainService = di.get<AssessmentDomainService>(
    AssessmentDomainService,
  );
  const selectedPlayerAssessments =
    assessmentDomainService.usePlayerAssessmentsSelector();

  const incident = assessmentDomainService.useSelectedPlayerIncidentSelector();

  const selectAssessment = (id: string) => {
    navigate(
      `/${getRouteByAccountType(
        loggedInUser?.accountType,
      )}/assessment-history?selectedAssessmentId=${id}`,
    );
  };

  const hasAssessments = selectedPlayerAssessments.length > 0;

  return (
    <Container>
      <Grid container>
        <Grid item xs={12} sm={12} md={8} lg={8}>
          <Outlet />
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={4} sx={{ mt: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mx: 2 }}>
            {t('LATEST_INCIDENT_REPORT')}
          </Typography>
          {incident !== undefined && incident.incidentDateTime ? (
            <IncidentReport incident={incident} />
          ) : (
            <Typography variant="body1" sx={{ mx: 2 }}>
              {t('incident.INCIDENT_REPORT_NO_INCIDENTS')}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mx: 2 }}>
            {t('PAST_ASSESSMENTS')}
          </Typography>

          {hasAssessments ? (
            <>
              {selectedPlayerAssessments.map((assessment) => (
                <Button
                  key={assessment.id}
                  type="button"
                  color="secondary"
                  variant="text"
                  size="medium"
                  fullWidth
                  onClick={() => selectAssessment(assessment.id)}
                  label={dateTime.format(
                    assessment.createdAt,
                    t('common.datetime.FORMAT_ABBREVIATED_SHORT_DATE'),
                  )}
                />
              ))}
            </>
          ) : (
            <Typography variant="body1" sx={{ mx: 2 }}>
              {t('incident.INCIDENT_REPORT_NO_ASSESSMENTS')}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
